import { useState, useEffect } from 'react'

// Module-level cache so every package list on the page shares ONE batch request
// per team (sidebar, course modal, teacher modal, mobile picker all read the same
// map) instead of each fetching per-package. Keyed by team id; holds the in-flight
// promise so concurrent callers dedupe onto it.
const teamCache = new Map() // teamId -> Promise<{ [packageId]: preview }>

const REFRESH_EVENT = 'discount-previews:refresh'

// Drop the team's cached previews and tell every mounted list to refetch. Call
// after anything that changes the student's discounts, such as claiming the
// welcome coupon without a page reload.
export function refreshDiscountPreviews(teamId) {
  teamCache.delete(teamId)
  window.dispatchEvent(new CustomEvent(REFRESH_EVENT, { detail: { teamId } }))
}

function fetchTeamPreviews(teamId) {
  if (!teamCache.has(teamId)) {
    const promise = fetch(`/api/teaching_teams/${teamId}/discount_previews`, { credentials: 'same-origin' })
      .then((res) => (res.ok ? res.json() : {}))
      .catch(() => {
        // Don't cache a failure permanently — allow a later mount to retry.
        teamCache.delete(teamId)
        return {}
      })
    teamCache.set(teamId, promise)
  }
  return teamCache.get(teamId)
}

// Returns the student's welcome/referral discount per package for the team, as a
// map { [packageId]: { original_amount, applied, welcome_applied, referral_applied,
// final_amount } }. Empty for logged-out visitors. Display-only — checkout always
// recomputes the authoritative amount server-side.
export function useDiscountPreviews(teamId, isLoggedIn) {
  const [previews, setPreviews] = useState({})
  const [version, setVersion] = useState(0)

  // A coupon claimed on this page changes every package's preview.
  useEffect(() => {
    const onRefresh = (event) => {
      if (event.detail?.teamId === teamId) setVersion((v) => v + 1)
    }
    window.addEventListener(REFRESH_EVENT, onRefresh)
    return () => window.removeEventListener(REFRESH_EVENT, onRefresh)
  }, [teamId])

  useEffect(() => {
    if (!isLoggedIn || !teamId) {
      setPreviews({})
      return
    }
    let cancelled = false
    fetchTeamPreviews(teamId).then((data) => {
      if (!cancelled) setPreviews(data || {})
    })
    return () => { cancelled = true }
  }, [teamId, isLoggedIn, version])

  return previews
}
